import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { WifiOff, UserX, ShieldCheck, Languages } from 'lucide-react';
import { StoreButtons } from './StoreButtons';

const NAV_LINKS = [
  ['#features', 'Features'],
  ['#privacy', 'Privacy'],
  ['#faq', 'FAQ'],
];

export const Nav = () => (
  <header className="lp-nav" data-testid="landing-nav">
    <a href="/" className="lp-brand" data-testid="nav-brand-link">
      <img src="/landing/icon.webp" alt="" width="34" height="34" />
      <span>ShiftReady</span>
    </a>
    <nav className="lp-nav__links">
      {NAV_LINKS.map(([href, label]) => (
        <a key={href} href={href} data-testid={`nav-${label.toLowerCase()}-link`}>{label}</a>
      ))}
    </nav>
    <a href="#download" className="lp-btn lp-btn--small" data-testid="nav-get-app-btn">Get the app</a>
  </header>
);

const useClock = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
};

const TRUST = [
  [WifiOff, 'Core tools work offline'],
  [UserX, 'No login'],
  [ShieldCheck, 'No cloud sync'],
  [Languages, '18 languages'],
];

const rise = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = () => {
  const clock = useClock();
  return (
    <section className="lp-hero" data-testid="hero-section">
      <div className="lp-hero__copy">
        <motion.div {...rise(0)} className="lp-pill" data-testid="hero-shift-clock">
          <span className="lp-dot" /> Shift active <span className="lp-mono">{clock}</span>
        </motion.div>
        <motion.h1 {...rise(0.08)} className="lp-h1">
          Your whole shift.<br /><em>One screen.</em>
        </motion.h1>
        <motion.p {...rise(0.16)} className="lp-lead">
          ShiftReady is the digital brain sheet for nurses. Track every patient, build your SBAR handoff in one tap,
          and clock out knowing your notes were saved on your phone, not on our servers, and are set to erase after your shift.
        </motion.p>
        <motion.div {...rise(0.24)}><StoreButtons prefix="hero" /></motion.div>
        <motion.ul {...rise(0.32)} className="lp-trust">
          {TRUST.map(([Icon, label]) => (
            <li key={label}><Icon size={16} /> {label}</li>
          ))}
        </motion.ul>
      </div>
      <div className="lp-hero__visual" aria-hidden="true">
        <motion.img
          src="/landing/02-sbar.webp" alt="" className="lp-shot lp-shot--back"
          initial={{ opacity: 0, x: 40, rotate: 8 }} animate={{ opacity: 1, x: 0, rotate: 6 }}
          transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        />
        <motion.img
          src="/landing/01-brainsheet.webp" alt="" className="lp-shot lp-shot--front"
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
    </section>
  );
};
